import { useSyncExternalStore } from 'react';
import { safeStorage } from './storage';

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

export const THEME_STORAGE_KEY = 'an-crm.theme';

const listeners = new Set<() => void>();

function readPreference(): ThemePreference {
  const v = safeStorage.get(THEME_STORAGE_KEY);
  return v === 'light' || v === 'dark' || v === 'system' ? v : 'system';
}

let preference: ThemePreference = readPreference();

function systemPrefersDark(): boolean {
  try {
    return typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch {
    return false;
  }
}

export function resolveTheme(pref: ThemePreference = preference): ResolvedTheme {
  if (pref === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return pref;
}

export function applyTheme(): void {
  if (typeof document === 'undefined') return;
  const resolved = resolveTheme();
  const root = document.documentElement;
  root.classList.toggle('dark', resolved === 'dark');
  root.style.colorScheme = resolved;
}

function emit(): void {
  applyTheme();
  for (const l of listeners) l();
}

export function getThemePreference(): ThemePreference {
  return preference;
}

export function setThemePreference(next: ThemePreference): void {
  preference = next;
  safeStorage.set(THEME_STORAGE_KEY, next);
  emit();
}

/** Flips between light and dark based on what is currently shown. */
export function toggleTheme(): void {
  setThemePreference(resolveTheme() === 'dark' ? 'light' : 'dark');
}

/** Re-applies the theme whenever the OS preference changes while in "system" mode. */
export function watchSystemTheme(): () => void {
  if (typeof window === 'undefined' || !window.matchMedia) return () => undefined;
  const mql = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    if (preference === 'system') emit();
  };
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/** test helper */
export function reloadThemePreference(): void {
  preference = readPreference();
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useTheme(): { preference: ThemePreference; resolved: ResolvedTheme } {
  const pref = useSyncExternalStore(subscribe, getThemePreference, getThemePreference);
  return { preference: pref, resolved: resolveTheme(pref) };
}
